/**
 * Payroll Approval Page.
 *
 * Lists payroll runs in pending-approval state and lets approvers act on
 * them. Modeled on OTApprovalPage but adapted to payroll's per-run shape
 * (vs. OT's per-DTR shape).
 *
 * See PRD_PayrollApproval.md §UI Surfaces — Payroll Approval Page.
 */

import React, { useState, useEffect, useMemo, useCallback, useId } from "react";
import { collection, query, where, getDocs, doc, getDoc, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../contexts/ToastContext";
import { approvePayroll, rejectPayroll } from "../services/payrollCompletionService";
import { loadRunEmployees, getRunSummary, runEmployeeCount } from "../services/payrollRunStore";
import PayrollRunExpansion from "./payroll/PayrollRunExpansion";
import ConfirmModal from "./modals/ConfirmModal";
import Modal from "./modals/Modal";
import "../styles/sections/payroll.css";
import { toYMDString } from "../utils/periodDate";

const formatPHP = (n) =>
  `₱${Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// 'YYYY-MM-DD' — the app-wide date standard (DESIGN-STANDARDS.md §13).
// toYMDString reads LOCAL Y/M/D components, never toISOString, so a Manila
// timestamp cannot render the previous day.
const formatDate = (val) => toYMDString(val) || "—";

const formatPeriodDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = typeof dateStr === "string" ? new Date(dateStr) : (dateStr.toDate ? dateStr.toDate() : new Date(dateStr));
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Normalize a periodStart (string 'YYYY-MM-DD' or Firestore Timestamp) to 'YYYY-MM'.
// Never call .slice on periodStart directly — it crashes on a Timestamp.
const toYM = (v) => {
  if (!v) return "";
  const d = v?.toDate ? v.toDate() : new Date(v);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Month dropdown: "All months" (default) + last 6 months. Default must be "all"
// — the page fetches ALL pending runs regardless of month, and an approver must
// see every run waiting, so a current-month default would hide off-month work.
const getMonthOptions = () => {
  const options = [{ value: "all", label: "All months" }];
  const now = new Date();
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
};
const MONTH_OPTIONS = getMonthOptions();

const STATUS_OPTIONS = [
  { value: "pending-approval", label: "Pending Approval" },
  { value: "completed", label: "Completed" },
  { value: "all", label: "All" },
];

// Status-neutral "who/when last acted on this run".
// pending → the submitter; completed → the approver. (rejected branch is
// defensive — rejected runs revert to draft and don't appear on this page.)
const actionedInfo = (run) => {
  if (run.status === "completed") return { by: run.approvalSnapshot?.approvedBy, at: run.approvalSnapshot?.approvedAt };
  if (run.status === "pending-approval") return { by: run.submittedBy, at: run.submittedAt };
  return { by: run.lastRejection?.by, at: run.lastRejection?.at };
};

// Normalize a Firestore Timestamp | Date | string to epoch ms (0 if absent).
const toMs = (v) => {
  if (!v) return 0;
  const d = v?.toDate ? v.toDate() : new Date(v);
  const t = d.getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Status chip — matches the PayrollList chip exactly (no leading dot).
const StatusBadge = ({ status }) => {
  if (status === "pending-approval") {
    return (
      <span className="pl-status-badge" style={{ background: "var(--ui-accent-tint)", color: "var(--ui-warning-text)", border: "1px solid var(--ui-accent)" }}>
        Pending Approval
      </span>
    );
  }
  if (status === "completed") {
    return <span className="pl-status-badge pl-status-badge--completed">Completed</span>;
  }
  return <span className="pl-status-badge pl-status-badge--draft">{status}</span>;
};

const PayrollApprovalPage = () => {
  const { user, company, hasPermission } = useAuth();
  const toast = useToast();
  const canApprove = hasPermission("payroll:approve");

  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [approvalType, setApprovalType] = useState("self-approval");
  const [statusFilter, setStatusFilter] = useState("pending-approval");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("actionedAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveModal, setApproveModal] = useState(null);
  const rejectTitleId = useId();

  const fetchData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);

    try {
      const [settingsSnap, userSnap, pendingSnap, completedSnap] = await Promise.all([
        getDoc(doc(db, "companies", company.id, "settings", "payrollRates")),
        getDocs(collection(db, "companies", company.id, "users")),
        getDocs(query(
          collection(db, "companies", company.id, "payrollRuns"),
          where("status", "==", "pending-approval"),
        )),
        getDocs(query(
          collection(db, "companies", company.id, "payrollRuns"),
          where("status", "==", "completed"),
          orderBy("lastModified", "desc"),
        )),
      ]);

      const settings = settingsSnap.exists() ? settingsSnap.data() : {};
      setApprovalType(settings.payrollApprovalType || "self-approval");

      const uMap = {};
      userSnap.docs.forEach((d) => {
        const data = d.data();
        uMap[d.id] = data.fullName || data.displayName || data.email || "Unknown User";
      });
      setUserMap(uMap);

      const docToRun = (d) => ({ runId: d.id, ...d.data() });
      const pending = pendingSnap.docs.map(docToRun).sort(
        (a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0),
      );
      const recentlyCompleted = completedSnap.docs.map(docToRun).slice(0, 25);

      setRuns([...pending, ...recentlyCompleted]);
    } catch (err) {
      console.error("[PayrollApprovalPage] Error fetching data:", err);
      toast.error("Failed to load payroll approval data");
    } finally {
      setLoading(false);
    }
  }, [company?.id, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Month + search filter only (NOT status) — drives the footer stats so the
  // pending/completed breakdown is truthful regardless of the active status tab.
  const monthSearchFiltered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return runs.filter((r) => {
      if (selectedMonth !== "all" && toYM(r.periodStart) !== selectedMonth) return false;
      if (term) {
        const periodStr = `${formatPeriodDate(r.periodStart)} ${formatPeriodDate(r.periodEnd)}`.toLowerCase();
        const hay = `${r.runId || ""} ${periodStr}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [runs, selectedMonth, searchTerm]);

  const stats = useMemo(() => ({
    total: monthSearchFiltered.length,
    pending: monthSearchFiltered.filter((r) => r.status === "pending-approval").length,
    completed: monthSearchFiltered.filter((r) => r.status === "completed").length,
  }), [monthSearchFiltered]);

  const sortValue = (run, field) => {
    switch (field) {
      case "period": return toMs(run.periodStart);
      case "runId": return run.runId || "";
      case "employees": return runEmployeeCount(run);
      case "totalNet": return getRunSummary(run).totalNet;
      case "actionedBy": {
        const by = actionedInfo(run).by;
        return (userMap[by] || by || "").toLowerCase();
      }
      case "actionedAt": return toMs(actionedInfo(run).at);
      case "status": return run.status || "";
      default: return 0;
    }
  };

  // Table rows: status filter applied to monthSearchFiltered, then sorted.
  const filteredRuns = useMemo(() => {
    const rows = monthSearchFiltered.filter(
      (r) => statusFilter === "all" || r.status === statusFilter,
    );
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortField);
      const bv = sortValue(b, sortField);
      // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
      // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
      // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
      const c = typeof av === "string" && typeof bv === "string"
        ? av.localeCompare(bv)
        : av < bv ? -1 : av > bv ? 1 : 0;
      return c * dir;
    });
    // sortValue closes over userMap, so it's a dep (actionedBy resolves names).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthSearchFiltered, statusFilter, sortField, sortDirection, userMap]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };
  const sortIndicator = (field) => {
    if (sortField !== field) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  // Maker-checker disable reason. Returns string when disabled, null otherwise.
  const getDisableReason = (run) => {
    if (run.status !== "pending-approval") return "This run has already been actioned";
    if (approvalType === "maker-checker") {
      const submitter = run.submittedBy;
      if (submitter && submitter === user?.userData?.userDocId) {
        return "Maker-checker is enabled. Ask another approver, or change the setting in Settings → Pay Rates.";
      }
    }
    return null;
  };

  const openApproveModal = (run) => {
    const reason = getDisableReason(run);
    if (reason) { toast.error(reason); return; }
    setApproveModal(run);
  };

  const handleApproveConfirm = async () => {
    if (!approveModal) return;
    const run = approveModal;
    setActionLoading((p) => ({ ...p, [run.runId]: "approve" }));
    try {
      // approvalType is intentionally NOT passed — the service re-reads the
      // policy from Firestore inside the transaction. UI-only checks are
      // bypassable (stale tab, direct service call); see PRD §E.
      // Hydrate the run's per-employee lines (deduction amounts) before approve —
      // approvePayroll's write-back reads run.employees, which the parent no
      // longer carries after conversion (C6). loadRunEmployees returns the inline
      // array for historical runs. (Reject needs only IDs — handled in the
      // service via run.selectedEmployeeIds.)
      const employees = await loadRunEmployees(company.id, run.runId || run.id, run);
      await approvePayroll({
        companyId: company.id,
        run: { ...run, employees },
        approverUserId: user.userData.userDocId,
      });
      toast.success("Payroll approved and completed.");
      setApproveModal(null);
      await fetchData();
      setExpandedRunId(null);
    } catch (err) {
      console.error("[PayrollApprovalPage] Approve failed:", err);
      toast.error(err.message || "Failed to approve payroll.");
      setApproveModal(null); // dismiss on error too (e.g. concurrent-action race)
      await fetchData();
    } finally {
      setActionLoading((p) => { const { [run.runId]: _, ...rest } = p; return rest; });
    }
  };

  const openRejectModal = (run) => {
    const reason = getDisableReason(run);
    if (reason) { toast.error(reason); return; }
    setRejectModal(run);
    setRejectReason("");
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      toast.error("A rejection reason is required.");
      return;
    }
    const run = rejectModal;
    setActionLoading((p) => ({ ...p, [run.runId]: "reject" }));
    try {
      await rejectPayroll({
        companyId: company.id,
        run,
        rejecterUserId: user.userData.userDocId,
        reason: trimmed,
      });
      toast.success("Payroll rejected. Returned to draft.");
      setRejectModal(null);
      setRejectReason("");
      await fetchData();
      setExpandedRunId(null);
    } catch (err) {
      console.error("[PayrollApprovalPage] Reject failed:", err);
      toast.error(err.message || "Failed to reject payroll.");
      setRejectModal(null); setRejectReason(""); // dismiss on error too (e.g. concurrent-action race)
      await fetchData();
    } finally {
      setActionLoading((p) => { const { [run.runId]: _, ...rest } = p; return rest; });
    }
  };

  if (loading) {
    return (
      <div>
        <h1 style={{ margin: 0, color: "var(--ui-primary)", fontSize: "1.25rem", fontWeight: 700 }}>Payroll Approval</h1>
        <p style={{ marginTop: 12, color: "var(--ui-text-muted)" }}>Loading payroll runs...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ margin: 0, color: "var(--ui-primary)", fontSize: "1.25rem", fontWeight: 700 }}>Payroll Approval</h1>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
          background: approvalType === "maker-checker" ? "var(--ui-primary-tint)" : "var(--ui-surface-recessed)",
          color: approvalType === "maker-checker" ? "var(--ui-primary)" : "var(--ui-text-secondary)",
          border: `1px solid ${approvalType === "maker-checker" ? "var(--ui-primary)" : "var(--ui-border)"}`,
        }}>
          Policy: {approvalType === "maker-checker" ? "Maker-checker" : "Self-approval"}
        </span>
      </div>

      {/* Controls bar — mirrors OTApprovalPage: search + month + status */}
      <div style={{
        display: "flex", alignItems: "center", gap: "0.75rem",
        padding: "0.625rem 1rem", background: "white", borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)", margin: "0.75rem 0",
        flexWrap: "wrap",
      }}>
        <input
          type="text"
          placeholder="Search by run ID or period..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 160, padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 8, fontSize: 14 }}
        />
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{ padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 8, fontSize: 14, background: "white" }}
        >
          {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: "0.5rem 0.75rem", border: "1px solid var(--ui-border)", borderRadius: 8, fontSize: 14, background: "white" }}
        >
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {!canApprove && (
        <div style={{ marginTop: 16, padding: 12, background: "var(--ui-accent-tint)", border: "1px solid var(--ui-accent)", borderRadius: 8, fontSize: 13 }}>
          You have read-only access to this page. The <code>payroll:approve</code> permission is required to approve or reject runs.
        </div>
      )}

      <div style={{ marginTop: 16, background: "var(--ui-surface)", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--ui-gradient-primary)" }}>
                {[
                  { key: "period", label: "Period", align: "left" },
                  { key: "runId", label: "Run ID", align: "left" },
                  { key: "employees", label: "Employees", align: "left" },
                  { key: "totalNet", label: "Total Net", align: "right" },
                  { key: "actionedBy", label: "Actioned By", align: "left" },
                  { key: "actionedAt", label: "Actioned At", align: "left" },
                  { key: "status", label: "Status", align: "left" },
                  { key: null, label: "Actions", align: "right" },
                ].map((col, i) => (
                  <th
                    key={i}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                    style={{
                      padding: "14px 16px", textAlign: col.align, fontWeight: 600,
                      fontSize: 12, textTransform: "uppercase", letterSpacing: "0.5px",
                      color: "white", cursor: col.key ? "pointer" : "default",
                      userSelect: "none", whiteSpace: "nowrap",
                    }}
                  >
                    {col.label}{col.key ? sortIndicator(col.key) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* The header stays up when there is nothing to show. An empty run
                  list is a normal state here, and the columns are what tell you
                  WHAT is absent — a bare sentence on a white card does not. Same
                  shape as OT Approval. colSpan tracks the 8 columns above. */}
              {filteredRuns.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 48, textAlign: "center", color: "var(--ui-text-muted)" }}>
                    {statusFilter === "pending-approval"
                      ? "No payroll runs are waiting for approval."
                      : "No payroll runs to display."}
                  </td>
                </tr>
              ) : filteredRuns.map((run) => {
                const isExpanded = expandedRunId === run.runId;
                const disableReason = getDisableReason(run);
                const actioned = actionedInfo(run);
                const actionedByName = actioned.by ? (userMap[actioned.by] || actioned.by) : "—";
                const loadingType = actionLoading[run.runId];
                const totalNet = getRunSummary(run).totalNet;
                // A submitted run always has ≥1 employee; 0/missing is a data-integrity
                // anomaly, not a valid state. Show '—' (don't launder it into a plausible
                // 0) and log it so it's traceable rather than invisible.
                const empCount = runEmployeeCount(run);
                if (!empCount) {
                  console.warn(`[PayrollApproval] run ${run.runId} has no employees`);
                }

                return (
                  <React.Fragment key={run.runId}>
                    <tr style={{ borderBottom: "1px solid var(--ui-surface-recessed)", cursor: "pointer" }}
                        onClick={() => setExpandedRunId(isExpanded ? null : run.runId)}>
                      <td style={{ padding: "12px 16px", fontSize: 14 }}>
                        {formatPeriodDate(run.periodStart)} – {formatPeriodDate(run.periodEnd)}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{run.runId}</td>
                      <td style={{ padding: "12px 16px", fontSize: 14 }}>{empCount || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 14, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatPHP(totalNet)}</td>
                      <td style={{ padding: "12px 16px", fontSize: 14 }}>{actionedByName}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "var(--ui-text-muted)" }}>{formatDate(actioned.at)}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <StatusBadge status={run.status} />
                        {run.status === "completed" && run.approvalSnapshot?.wasSelfApproval && (
                          <span style={{ marginLeft: 6, fontSize: 11, padding: "2px 6px", background: "var(--ui-accent-tint)", border: "1px solid var(--ui-accent)", borderRadius: 4, color: "var(--ui-warning-text)" }}>
                            Self-approved
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                        {run.status === "pending-approval" && canApprove ? (
                          <div style={{ display: "inline-flex", gap: 8 }}>
                            <button
                              type="button"
                              disabled={!!disableReason || !!loadingType}
                              title={disableReason || ""}
                              onClick={() => openApproveModal(run)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "1px solid var(--ui-success)",
                                background: disableReason ? "var(--ui-surface-recessed)" : "var(--ui-success)",
                                color: disableReason ? "var(--ui-text-faint)" : "var(--ui-surface)",
                                cursor: disableReason ? "not-allowed" : "pointer",
                                fontSize: 13,
                                fontWeight: 500,
                              }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={!!disableReason || !!loadingType}
                              title={disableReason || ""}
                              onClick={() => openRejectModal(run)}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "1px solid var(--ui-danger)",
                                background: disableReason ? "var(--ui-surface-recessed)" : "var(--ui-surface)",
                                color: disableReason ? "var(--ui-text-faint)" : "var(--ui-danger)",
                                cursor: disableReason ? "not-allowed" : "pointer",
                                fontSize: 13,
                                fontWeight: 500,
                              }}
                            >
                              Reject
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ padding: 0, background: "var(--ui-bg)" }}>
                          <PayrollRunExpansion run={run} companyId={company.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
        </table>

        {/* Footer stats — computed over month+search-filtered set (pre status filter) */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "16px 20px",
          background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
          borderTop: "1px solid var(--ui-border)", flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ display: "flex", gap: 28 }}>
            {[
              { label: "Total Records", value: stats.total, color: "var(--ui-primary)" },
              { label: "Pending Approval", value: stats.pending, color: "var(--ui-warning)" },
              { label: "Completed", value: stats.completed, color: "var(--ui-success)" },
            ].map((s) => (
              <div key={s.label}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--ui-text-muted)" }}>{s.label}</div>
                <div style={{ fontSize: 20, fontWeight: 600, color: s.color, fontVariantNumeric: "tabular-nums" }}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 13, fontStyle: "italic", color: "var(--ui-text-muted)" }}>
            Click on a row to view details
          </div>
        </div>
      </div>

      {approveModal && (() => {
        const isSelf = approveModal.submittedBy === user?.userData?.userDocId && approvalType === "self-approval";
        const message =
          `${isSelf ? "You are approving your own submission — this will be recorded as a self-approval. " : ""}` +
          `Approving finalizes this payroll run and locks the pay period. This action cannot be undone.`;
        return (
          <ConfirmModal
            isOpen
            onClose={() => setApproveModal(null)}
            onConfirm={handleApproveConfirm}
            title="Approve Payroll Run"
            message={message}
            confirmText="Approve"
            cancelText="Cancel"
            variant="warning"
            loading={actionLoading[approveModal.runId] === "approve"}
          />
        );
      })()}

      {rejectModal && (
        <Modal
          isOpen
          onClose={() => { setRejectModal(null); setRejectReason(""); }}
          busy={!!actionLoading[rejectModal.runId]}
          labelledBy={rejectTitleId}
          style={{ background: "var(--ui-surface)", borderRadius: 12, padding: 24, width: "min(500px, 92vw)" }}
        >
            <h3 id={rejectTitleId} style={{ margin: 0, color: "var(--ui-text)" }}>Reject Payroll Run</h3>
            <p style={{ marginTop: 8, color: "var(--ui-text-muted)", fontSize: 14 }}>
              This will revert the run to draft. The submitter will see the rejection
              reason and can edit and re-submit.
            </p>
            <label style={{ display: "block", marginTop: 12, fontSize: 13, fontWeight: 500, color: "var(--ui-text-secondary)" }}>
              Reason (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              autoFocus
              rows={4}
              style={{
                width: "100%", marginTop: 4, padding: 10, borderRadius: 6,
                border: "1px solid var(--ui-border)", fontSize: 14, fontFamily: "inherit",
                resize: "vertical",
              }}
              placeholder="Explain what needs to be fixed before re-submission..."
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setRejectModal(null); setRejectReason(""); }}
                disabled={!!actionLoading[rejectModal.runId]}
                style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--ui-border)", background: "var(--ui-surface)", color: "var(--ui-text-secondary)", cursor: "pointer", fontSize: 14 }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || !!actionLoading[rejectModal.runId]}
                style={{
                  padding: "8px 16px", borderRadius: 6, border: "1px solid var(--ui-danger)",
                  background: rejectReason.trim() ? "var(--ui-danger)" : "var(--ui-surface-recessed)",
                  color: rejectReason.trim() ? "var(--ui-surface)" : "var(--ui-text-faint)",
                  cursor: rejectReason.trim() ? "pointer" : "not-allowed",
                  fontSize: 14, fontWeight: 500,
                }}
              >
                {actionLoading[rejectModal.runId] === "reject" ? "Rejecting..." : "Reject Payroll"}
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default PayrollApprovalPage;
